import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wind, Activity, Sliders, Eye, CheckCircle, Send, ArrowRight } from 'lucide-react';

const PollutionControl = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    facilityType: '',
    pollutantTypes: '',
    emissionSource: '',
    currentControl: '',
    regulatoryDriver: '',
    performanceGoals: '',
    budgetConstraints: '',
    timeline: '',
    specificRequirements: '',
    additionalInfo: ''
  });

  const facilityTypes = [
    { value: '', label: 'Select facility type' },
    { value: 'power-generation', label: 'Power Generation' },
    { value: 'oil-gas', label: 'Oil & Gas' },
    { value: 'industrial', label: 'Industrial/Manufacturing' },
    { value: 'marine', label: 'Marine/Seafood' },
    { value: 'other', label: 'Other' }
  ];

  const pollutantTypes = [
    { value: '', label: 'Select primary pollutants' },
    { value: 'particulate', label: 'Particulate Matter (PM)' },
    { value: 'nox', label: 'Nitrogen Oxides (NOx)' },
    { value: 'sox', label: 'Sulfur Oxides (SOx)' },
    { value: 'voc', label: 'Volatile Organic Compounds (VOC)' },
    { value: 'co', label: 'Carbon Monoxide (CO)' },
    { value: 'haps', label: 'Hazardous Air Pollutants (HAPs)' },
    { value: 'multiple', label: 'Multiple Pollutants' }
  ];

  const regulatoryDrivers = [
    { value: '', label: 'Select regulatory driver' },
    { value: 'bact', label: 'BACT Requirements' },
    { value: 'nsps', label: 'NSPS Compliance' },
    { value: 'neshap', label: 'NESHAP Standards' },
    { value: 'permit-limits', label: 'Permit Limit Compliance' },
    { value: 'voluntary', label: 'Voluntary Reduction' },
    { value: 'other', label: 'Other' }
  ];

  const serviceDeliverables = [
    {
      title: 'BACT & RACT Analysis',
      description: 'Top-down analysis to select control technologies (Oxidizers, Scrubbers, Baghouses) that satisfy EPA requirements at the lowest achievable cost.',
      icon: Wind
    },
    {
      title: 'Stack Testing Management',
      description: 'On-site coordination of source testing vendors, protocol development, and validation of test results for compliance certification.',
      icon: Activity
    },
    {
      title: 'Control Optimization',
      description: 'Troubleshooting existing control devices to reduce reagent usage and prevent excess emission events.',
      icon: Sliders
    },
    {
      title: 'Visibility & Haze Strategy',
      description: 'Modeling and mitigation strategies for Regional Haze and Class I area visibility impacts.',
      icon: Eye
    }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/pollution-control`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your pollution control inquiry has been submitted successfully. We will contact you shortly.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          facilityType: '', pollutantTypes: '', emissionSource: '', currentControl: '',
          regulatoryDriver: '', performanceGoals: '', budgetConstraints: '', timeline: '', 
          specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    }
  };

  const technologies = [
    {
      category: 'Particulate Control',
      description: 'Technologies for reducing particulate matter emissions.',
      methods: ['Fabric filters (baghouses)', 'Electrostatic precipitators (ESP)', 'Cyclones', 'Wet scrubbers']
    },
    {
      category: 'NOx Control',
      description: 'Nitrogen oxide reduction technologies.',
      methods: ['Selective Catalytic Reduction (SCR)', 'Selective Non-Catalytic Reduction (SNCR)', 'Low-NOx burners', 'Combustion optimization']
    },
    {
      category: 'VOC Control',
      description: 'Volatile organic compound control systems.',
      methods: ['Thermal oxidizers', 'Catalytic oxidizers', 'Carbon adsorption', 'Condensers']
    },
    {
      category: 'SOx Control',
      description: 'Sulfur dioxide reduction methods.',
      methods: ['Wet scrubbers (FGD)', 'Dry sorbent injection', 'Low-sulfur fuel', 'Fuel desulfurization']
    }
  ];

  const bactAnalysis = [
    'Identification of all available control technologies',
    'Technical feasibility assessment for Alaska conditions',
    'Energy, environmental, and economic impact analysis',
    'Ranking of control options by effectiveness',
    'Cost-effectiveness calculations',
    'Recommended BACT determination with justification'
  ];

  return (
    <div className="min-h-screen" data-testid="pollution-control-page">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGxhbnR8ZW58MHx8fHwxNzYxMTAxMTkxfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Pollution Control & BACT Analysis
            </h1>
            <p className="text-xl text-gray-100 mb-8">
              Strategic evaluation of Best Available Control Technologies (BACT) and stack testing oversight. We help you meet emission limits without over-capitalizing on unnecessary hardware.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact-form"
                className="inline-flex items-center px-6 py-3 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors shadow-lg"
              >
                Request BACT Analysis
                <Send className="ml-2" size={20} />
              </a>
              <a
                href="#key-deliverables"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                View Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Comprehensive Pollution Control Solutions</h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Selecting and implementing the right pollution control technology is essential for regulatory compliance and environmental stewardship. Our expertise in BACT analysis and control technology evaluation ensures you implement cost-effective solutions that meet or exceed all requirements.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We evaluate all available control technologies, considering Alaska's unique operational challenges, and recommend optimal solutions balancing performance, reliability, and cost.
            </p>
          </div>
        </div>
      </section>

      {/* Key Deliverables - Cyan Jewel Icons */}
      <section id="key-deliverables" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Key Deliverables
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert emissions control technology evaluation and stack testing management
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {serviceDeliverables.map((deliverable, index) => {
              const IconComponent = deliverable.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-cyan-50 to-teal-50 p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow">
                  {/* Jewel Style Icon - Cyan */}
                  <div className="bg-cyan-600 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{deliverable.title}</h3>
                  <p className="text-gray-600">{deliverable.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Control Technologies We Evaluate</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md border-t-4 border-cyan-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{tech.category}</h3>
                <p className="text-gray-600 mb-4">{tech.description}</p>
                <ul className="space-y-2">
                  {tech.methods.map((method, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-cyan-600 mr-2 flex-shrink-0 mt-0.5" />
                      {method}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BACT Analysis */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">BACT Analysis Process</h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-600 mb-8">
              Best Available Control Technology (BACT) analysis is a systematic process required for major new sources and modifications. Our comprehensive BACT reviews include:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bactAnalysis.map((step, index) => (
                <div key={index} className="flex items-start bg-teal-50 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-teal-700 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="contact-form" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-cyan-600 text-white p-8 rounded-t-xl">
              <div className="flex items-center mb-4">
                <Send className="w-10 h-10 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold">Request BACT Analysis & Stack Testing</h2>
                  <p className="text-cyan-100 mt-2">Get expert evaluation of control technologies and emissions testing coordination</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl border border-gray-200">
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Facility & Emission Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Facility Type</label>
                    <select name="facilityType" value={formData.facilityType} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                      {facilityTypes.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Primary Pollutants</label>
                    <select name="pollutantTypes" value={formData.pollutantTypes} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                      {pollutantTypes.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Emission Source Description</label>
                  <textarea name="emissionSource" value={formData.emissionSource} onChange={handleChange} rows="2" placeholder="Describe emission sources (engines, boilers, process equipment)" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"></textarea>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Controls</label>
                    <input type="text" name="currentControl" value={formData.currentControl} onChange={handleChange} placeholder="Existing control equipment" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Regulatory Driver</label>
                    <select name="regulatoryDriver" value={formData.regulatoryDriver} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                      {regulatoryDrivers.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Performance Goals</label>
                  <input type="text" name="performanceGoals" value={formData.performanceGoals} onChange={handleChange} placeholder="Target emission levels or reduction percentage" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Project Constraints</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Budget Constraints</label>
                    <input type="text" name="budgetConstraints" value={formData.budgetConstraints} onChange={handleChange} placeholder="Budget range or limitations" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Implementation Timeline</label>
                    <input type="text" name="timeline" value={formData.timeline} onChange={handleChange} placeholder="When controls need to be operational" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Requirements or Concerns <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="specificRequirements"
                    value={formData.specificRequirements}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="e.g., BACT analysis needs, emission control technology preferences, regulatory compliance challenges, control efficiency targets, space limitations, operational constraints, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                  <textarea name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} rows="4" placeholder="Any other details, constraints, or questions" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"></textarea>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <button type="submit" className="inline-flex items-center px-8 py-4 bg-cyan-600 text-white rounded-lg font-semibold hover:bg-cyan-700 transition-colors shadow-lg">
                  Submit BACT Analysis Request
                  <Send className="ml-2" size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Meet Emission Limits Without Over-Capitalizing
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Strategic BACT analysis and stack testing oversight for cost-effective compliance
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
          >
            Get Expert Control Technology Guidance
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PollutionControl;
